import { Injectable } from '@angular/core';
import { AbstractControl, AsyncValidatorFn, FormArray, FormGroup, ValidationErrors, ValidatorFn } from '@angular/forms';
import { Store } from '@ngrx/store';
import { Actions, ofType } from '@ngrx/effects';
import { Observable, of } from 'rxjs';
import { map, tap } from 'rxjs/operators';
import { ValidatorIdentifier } from '../models/validator-identifier';
import { LocationCardState } from '../reducers';
import { BasicInfoCommonFieldIds, CommonSectionIds, DataDeliveryFieldId, PipeInstallationFieldIds, WeirInstallationFieldIds } from '../configs/common.models';
import { LocationService } from 'app/shared/services/location.service';
import { findFieldById } from '../configs/base-utils';
import { FieldType, SelectField } from '../models/field-models';
import { BASE_MONITOR_TO_ENUM_MAP, BaseMonitorSeriesValues, MonitorSeriesNames } from '../../location-card.constants';
import { setGlobalConfig } from '../actions/form-actions';
import { INSTALLATION_TYPE } from 'app/shared/models/location-details';

interface AsyncValidatorCache {
    lastValue: string | number | boolean | Date;
    lastResult: ValidationErrors | null;
}

@Injectable({
    providedIn: 'root',
})
export class ValidatorRegistryService {
    public asyncValidatorCache = new Map<string, AsyncValidatorCache>();

    constructor(private store: Store, private locationService: LocationService, private actions$: Actions) {
        this.setupMonitorSeriesChangeListener();
    }


    getValidatorByIdentifier(identifier: ValidatorIdentifier, state: LocationCardState<unknown>): ValidatorFn {
        const validatorMap: { [K in ValidatorIdentifier]?: (state: LocationCardState<unknown>) => ValidatorFn } = {
            [ValidatorIdentifier.UniqueLocationName]: this.uniqueLocationNameValidator.bind(this, identifier, state),
            [ValidatorIdentifier.CustomPropUniqueKey]: this.customPropertyUniqueKeyValidator.bind(this, identifier),
            [ValidatorIdentifier.FastRateValidator]: this.fastRateValidator.bind(this, identifier, state),
            [ValidatorIdentifier.NormalRateValidator]: this.normalRateValidator.bind(this, identifier, state),
            [ValidatorIdentifier.ManholeDepthToPipeHeightValidator]: this.manholeDepthToPipeHeightValidator.bind(this, identifier, state),
            [ValidatorIdentifier.DeliveryRateValidator]: this.deliveryRateValidator.bind(this, identifier, state),
        };

        const validator = validatorMap[identifier];

        if (!validator) {
            console.error(`Sync validator not found for identifier: ${identifier}`);
        }

        return validator(state);
    }

    getAsyncValidatorByIdentifier(identifier: ValidatorIdentifier, state: LocationCardState<unknown>): AsyncValidatorFn {
        const asyncValidatorMap: { [K in ValidatorIdentifier]?: (state: LocationCardState<unknown>) => AsyncValidatorFn } = {
            [ValidatorIdentifier.UniqueSerialNumber]: this.uniqueSerialNumberAsyncValidator.bind(
                this,
                identifier,
                state,
            ),
        };

        const asyncValidator = asyncValidatorMap[identifier];

        if (!asyncValidator) {
            console.error(`Async validator not found for identifier: ${identifier}`);
        }

        return this.createCachedAsyncValidator(identifier, asyncValidator(state));
    }

    private uniqueLocationNameValidator(identifier: ValidatorIdentifier, state: LocationCardState<unknown>): ValidatorFn {
        return (control: AbstractControl): ValidationErrors | null => {
            const locationName: string = control.value;
            if (!locationName || !state.locations) return null;

            const isEditMode = state.locationDetails && state.locationDetails.length;
            const isDuplicate = !!state.locations.find((location) => {
                if (!isEditMode) {
                    return location.locationName?.toLowerCase().trim() === locationName.toLowerCase().trim();
                }

                return (
                    location.locationName?.toLowerCase().trim() === locationName.toLowerCase().trim() &&
                    !state.locationDetails.some((detail) => detail.locationID === location.locationId)
                );
            });

            return isDuplicate ? { [identifier]: true } : null;
        };
    }

    private customPropertyUniqueKeyValidator(identifier: ValidatorIdentifier, state: LocationCardState<unknown>): ValidatorFn {
        return (control: AbstractControl): ValidationErrors | null => {
            if (!control.value) return null;

            const customPropGroup = control.parent as FormGroup;

            if (!customPropGroup) return null;

            const custmPropSection = customPropGroup.parent as FormArray;

            if (!custmPropSection) return null;

            const currentIndex = custmPropSection.controls.indexOf(customPropGroup);

            const allKeys = custmPropSection
                .getRawValue()
                .map((item, index) => item[`${BasicInfoCommonFieldIds.CustomPropertyKey}-${index}`]);

            const isDuplicate = allKeys.indexOf(control.value) !== currentIndex;

            return isDuplicate ? { [identifier]: true } : null;
        };
    }

    private fastRateValidator(identifier: ValidatorIdentifier, state: LocationCardState<unknown>): ValidatorFn {
        return (control: AbstractControl): ValidationErrors | null => {
            if (!control) return null;

            const formState = state.formState;
            const basicInfo = formState.config.sections.find((section) => section.id === CommonSectionIds.BasicInfo);

            if (!basicInfo) return null;

            const normalRate = basicInfo.fields[BasicInfoCommonFieldIds.SampleRate];

            if (!normalRate) return null;

            const isInvalid = control.value > normalRate.value;
            return isInvalid ? { [identifier]: true } : null;
        };
    }

    private normalRateValidator(identifier: ValidatorIdentifier, state: LocationCardState<unknown>): ValidatorFn {
        return (control: AbstractControl): ValidationErrors | null => {
            if (!control) return null;

            const formState = state.formState;
            const basicInfo = formState.config.sections.find((section) => section.id === CommonSectionIds.BasicInfo);

            if (!basicInfo) return null;

            const fastRate = basicInfo.fields[BasicInfoCommonFieldIds.FastRate];

            if (!fastRate) return null;

            const isInvalid = control.value < fastRate.value;

            return isInvalid ? { [identifier]: true } : null;
        };
    }

    private uniqueSerialNumberAsyncValidator(
        identifier: ValidatorIdentifier,
        state: LocationCardState<unknown>,
    ): AsyncValidatorFn {
        return (control: AbstractControl): Observable<ValidationErrors | null> => {
            if (!control.value) {
                return of(null);
            }

            const cid = state.customer.customerID;
            const isEditMode = state.locationDetails && state.locationDetails.length;
            const locationId = isEditMode ? state.locationDetails[0].locationID : 0;

            const monitorSeriesField = findFieldById(
                state.formState,
                BasicInfoCommonFieldIds.Series,
                FieldType.Select,
            ) as SelectField<MonitorSeriesNames, BaseMonitorSeriesValues>;

            const series = BASE_MONITOR_TO_ENUM_MAP[monitorSeriesField.value];

            return this.locationService
                .validateSerialNumber(cid, locationId, series, control.value)
                .pipe(map((isUnique: boolean) => (isUnique ? null : { [identifier]: true })));
        };
    }

    private manholeDepthToPipeHeightValidator(identifier: ValidatorIdentifier, state: LocationCardState<unknown>): ValidatorFn {
        return (control: AbstractControl): ValidationErrors | null => {
            if (!control || !control.value) return null;

            const pipeHeight = this.getPipeHeightValue(state);

            if (!pipeHeight) return null;

            if (Number(control.value) < Number(pipeHeight)) {
                return { [identifier]: true };
            }

            return null;
        };
    }

    private deliveryRateValidator(identifier: ValidatorIdentifier, state: LocationCardState<unknown>): ValidatorFn {
        return (control: AbstractControl): ValidationErrors | null => {
            if (!control) return null;

            const formState = state.formState;
            const dataDeliverySection = formState.config.sections.find(
                (section) => section.id === CommonSectionIds.DataDelivery || section.fields[DataDeliveryFieldId.PrismDeliveryNormal],
            );

            if (!dataDeliverySection) return null;

            const fastField = dataDeliverySection.fields[DataDeliveryFieldId.PrismDeliveryFast];
            const normalField = dataDeliverySection.fields[DataDeliveryFieldId.PrismDeliveryNormal];

            if (!fastField || !normalField) return null;

            const fastValue = fastField.value as number;
            const normalValue = normalField.value as number;

            if (fastValue === null || normalValue === null || fastValue === 0 || normalValue === 0) {
                return null;
            }

            return fastValue > normalValue ? { [identifier]: true } : null;
        };
    }

    // since we recreate fields on form state change, we need to cache async validators, to not call the API every time
    private createCachedAsyncValidator(
        identifier: ValidatorIdentifier,
        originalValidator: AsyncValidatorFn,
    ): AsyncValidatorFn {
        return (control: AbstractControl): Observable<ValidationErrors | null> => {
            const currentValue = control.value;
            const cached = this.asyncValidatorCache.get(identifier);

            if (cached && cached.lastValue === currentValue) {
                return of(cached.lastResult);
            }

            return (originalValidator(control) as Observable<ValidationErrors | null>).pipe(
                tap((result) => {
                    this.asyncValidatorCache.set(identifier, {
                        lastValue: currentValue,
                        lastResult: result,
                    });

                    // Trigger update of the control to ensure validation is applied immediately
                    setTimeout(() => {
                        control.updateValueAndValidity({ onlySelf: true });
                    }, 0);
                }),
            );
        };
    }

    private setupMonitorSeriesChangeListener(): void {
        this.actions$
            .pipe(
                ofType(setGlobalConfig),
                tap(() => {
                    this.locationService.bustCache();
                    this.asyncValidatorCache.clear();
                }),
            )
            .subscribe();
    }

    private getPipeHeightValue(state: LocationCardState<unknown>): number {
        const basicInfo = state.formState.config.sections.find((s) => s.id === CommonSectionIds.BasicInfo);
    
        const installationType = (basicInfo?.fields[BasicInfoCommonFieldIds.InstallationType].value) as string;
    
        if (!installationType) {
            return 0;
        }
    
        const [type] = installationType.split(':').map((s) => s.trim());
    
        if (type === INSTALLATION_TYPE.PIPE) {
            return findFieldById(state.formState, PipeInstallationFieldIds.Height, FieldType.Number)?.value ?? 0;
        }
    
        if (type === INSTALLATION_TYPE.WEIR) {
            return findFieldById(state.formState, WeirInstallationFieldIds.Height, FieldType.Number)?.value ?? 0;
        }
    
        return 0;
    }
    
}
